/**
 * Generación del PDF Global por Proceso.
 * Reporte de afinidad de candidatos agrupados por proceso.
 */

import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';

export interface ProcessGlobalCandidate {
  candidateName?: string;
  status: 'in_progress' | 'completed';
  completedAt?: string;
  startedAt: string;
  globalMatch?: number;
}

function getMatchLabel(score: number): string {
  if (score >= 80) return 'Alineación Alta';
  if (score >= 60) return 'Alineación Media';
  return 'Brecha Amplia';
}

function formatAffinity(c: ProcessGlobalCandidate): string {
  if (c.status !== 'completed' || c.globalMatch == null) return '—';
  return `${c.globalMatch}% - ${getMatchLabel(c.globalMatch)}`;
}

function formatDate(c: ProcessGlobalCandidate): string {
  const raw = c.completedAt || c.startedAt || '';
  if (!raw) return '—';
  try {
    return new Date(raw).toLocaleDateString('es-MX', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return raw;
  }
}

function formatStatus(c: ProcessGlobalCandidate): string {
  return c.status === 'completed' ? 'Completado' : 'En curso';
}

function sanitizeFilename(s: string): string {
  return s.replace(/[<>:"/\\|?*]/g, '_').replace(/\s+/g, '_').slice(0, 80);
}

/**
 * Genera y descarga el PDF del reporte global de afinidad por proceso.
 */
export async function generateProcessGlobalPdf(
  processTitle: string,
  candidates: ProcessGlobalCandidate[],
  companyName: string,
  generatedBy: string
): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 15;
  let y = margin;

  // Colores corporativos: azul oscuro neutro
  const primaryColor: [number, number, number] = [30, 58, 138]; // blue-900
  const headerBg: [number, number, number] = [30, 58, 138];
  const headerText: [number, number, number] = [255, 255, 255];

  // ─── Encabezado ───
  const displayCompanyName = (companyName || 'Empresa').trim() || 'Empresa';
  const displayGeneratedBy = (generatedBy || 'Usuario').trim() || 'Usuario';

  doc.setFontSize(11);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 58, 138);
  doc.text(displayCompanyName, margin, y);
  y += 5;

  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(`Generado por: ${displayGeneratedBy}`, margin, y);
  y += 7;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  doc.text('Reporte Global de Afinidad', margin, y);
  y += 6;

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...primaryColor);
  doc.text(processTitle, margin, y);
  doc.setTextColor(0, 0, 0);
  y += 10;

  // ─── Tabla con autoTable ───
  const head = [['Candidato', 'Estado', 'Fecha', 'Afinidad al Puesto']];
  const body = candidates.map((c) => [
    (c.candidateName ?? 'Candidato').trim() || '—',
    formatStatus(c),
    formatDate(c),
    formatAffinity(c),
  ]);

  autoTable(doc, {
    head,
    body,
    startY: y,
    theme: 'grid',
    headStyles: {
      fillColor: headerBg,
      textColor: headerText,
      fontStyle: 'bold',
      fontSize: 9,
    },
    styles: { fontSize: 9 },
    columnStyles: {
      0: { cellWidth: 50 },
      1: { cellWidth: 28 },
      2: { cellWidth: 30 },
      3: { cellWidth: 'auto' },
    },
    margin: { left: margin, right: margin },
  });

  const finalY = (doc as jsPDF & { lastAutoTable?: { finalY?: number } }).lastAutoTable?.finalY ?? y;
  y = finalY + 12;

  // Pie de página
  if (y > 270) {
    doc.addPage();
    y = margin;
  }
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 116, 139);
  doc.text(
    `Generado el ${new Date().toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' })} · PsyTalent by PsyTech Labs`,
    margin,
    y
  );

  const safeTitle = sanitizeFilename(processTitle);
  doc.save(`Reporte_${safeTitle}.pdf`);
}
